import type {
	PluginComponentConfig,
	PluginComponentNames,
	SinglePluginComponentConfig
} from '../types';

/**
 * Function that helps you to capitalize the first letter on a word
 */
export function capitalizeFirstLetter(text: string): string {
	return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Function that helps you to get all non-nullable props from a component
 *
 * @param props the props object
 * @param vueInst the Vue component instance
 *
 * @internal
 */
export function getPropsValuesWithoutOptionsProp(
	props: any,
	vueInst?: ComponentPublicInstance
): Record<string, any> {
	const invalidKeys: string[] = ['options', 'bounds'];
	const keys = Object.keys(props) as string[];

	return keys.reduce((acc, propKey) => {
		const hasValidKey = !invalidKeys.includes(propKey);
		const value = vueInst
			? (vueInst?.$props as any)[propKey]
			: (props as any)[propKey];
		const hasValidValue = value != null;

		return hasValidKey && hasValidValue ? { ...acc, [propKey]: value } : acc;
	}, {});
}

const componentConfigs: PluginComponentConfig = {
	GmpMap: {
		noBind: ['resizeBus', 'center', 'zoom', 'options'],
		twoWay: ['heading', 'mapTypeId', 'tilt'],
		events: {
			/**
			 * @see https://developers.google.com/maps/documentation/javascript/reference/map#Map-Events
			 */
			auto: [
				'click',
				'contextmenu',
				'dblclick',
				'drag',
				'dragend',
				'dragstart',
				'heading_changed',
				'idle',
				'isfractionalzoomenabled_changed',
				'mapcapabilities_changed',
				'maptypeid_changed',
				'mousemove',
				'mouseout',
				'mouseover',
				'projection_changed',
				'renderingtype_changed',
				'tilesloaded',
				'tilt_changed',
				'zoom_changed',
				'resize'
			],
			manual: ['bounds_changed', 'center_changed', 'zoom_changed']
		}
	},
	GmpMarker: {
		noBind: [],
		twoWay: [
			'animation',
			'clickable',
			'cursor',
			'draggable',
			'icon',
			'position',
			'shape',
			'title',
			'visible',
			'zIndex'
		],
		events: {
			auto: ['click', 'drag', 'dragstart', 'gmp-click'],
			manual: ['dragend', 'update:position']
		}
	},
	GmpCluster: {
		noBind: [],
		twoWay: [],
		events: {
			auto: [
				'clusteringbegin',
				'clusteringend',
				'click',
				'rightclick',
				'dblclick',
				'drag',
				'dragstart',
				'dragend',
				'mouseup',
				'mousedown',
				'mouseover',
				'mouseout'
			],
			manual: []
		}
	},
	GmpInfoWindow: {
		noBind: ['ariaLabel', 'options'],
		twoWay: [
			'content',
			'disableAutoPan',
			'maxWidth',
			'minWidth',
			'pixelOffset',
			'position',
			'zIndex'
		],
		events: {
			/**
			 * @see https://developers.google.com/maps/documentation/javascript/reference/info-window#InfoWindow-Events
			 */
			auto: [
				'close',
				'closeclick',
				'content_changed',
				'domready',
				'position_changed',
				'visible',
				'zindex_changed'
			],
			manual: []
		}
	},
	GmpAutocomplete: {
		noBind: ['componentRestrictions'],
		twoWay: [],
		events: {
			auto: [],
			manual: ['place_changed']
		}
	},
	GmpKmlLayer: {
		noBind: [
			'clickable',
			'preserveViewport',
			'screenOverlays',
			'suppressInfoWindows'
		],
		twoWay: ['map', 'zIndex'],
		events: {
			auto: ['click', 'defaultviewport_changed', 'status_changed'],
			manual: []
		}
	},
	GmpStreetViewPanorama: {
		noBind: ['position'],
		twoWay: ['pov', 'pano', 'visible'],
		events: {
			auto: [
				'closeclick',
				'pano_changed',
				'position_changed',
				'pov_changed',
				'resize',
				'status_changed',
				'visible_changed',
				'zoom_changed'
			],
			manual: ['position_changed']
		}
	},
	GmpHeatmapLayer: {
		noBind: [],
		twoWay: ['data'],
		events: {
			auto: ['data_changed'],
			manual: []
		}
	},
	GmpCircle: {
		noBind: [
			'clickable',
			'fillColor',
			'fillOpacity',
			'strokeColor',
			'strokeOpacity',
			'strokePosition',
			'strokeWeight',
			''
		],
		twoWay: ['center', 'radius'],
		events: {
			auto: [
				'center_changed',
				'click',
				'dblclick',
				'drag',
				'dragend',
				'dragstart',
				'mousedown',
				'mousemove',
				'mouseout',
				'mouseover',
				'mouseup',
				'radius_changed',
				'rightclick'
			],
			manual: []
		}
	},
	GmpPolygon: {
		noBind: [
			'clickable',
			'fillColor',
			'fillOpacity',
			'strokeColor',
			'strokeOpacity',
			'strokePosition',
			'strokeWeight',
			'path',
			'paths'
		],
		twoWay: [],
		events: {
			auto: [
				'click',
				'contextmenu',
				'dblclick',
				'drag',
				'dragend',
				'dragstart',
				'mousedown',
				'mousemove',
				'mouseout',
				'mouseover',
				'mouseup'
			],
			manual: []
		}
	},
	GmpPolyline: {
		noBind: ['clickable', 'strokeColor', 'strokeOpacity', 'strokeWeight'],
		twoWay: ['path'],
		events: {
			/**
			 * @see https://developers.google.com/maps/documentation/javascript/reference/polygon?hl=es#Polyline-Events
			 */
			auto: [
				'click',
				'contextmenu',
				'dblclick',
				'drag',
				'dragend',
				'dragstart',
				'mousedown',
				'mousemove',
				'mouseout',
				'mouseover',
				'mouseup'
			],
			manual: []
		}
	},
	GmpRectangle: {
		noBind: [
			'clickable',
			'fillColor',
			'fillOpacity',
			'strokeColor',
			'strokeOpacity',
			'strokePosition',
			'strokeWeight'
		],
		twoWay: ['bounds', ''],
		events: {
			/**
			 * @see https://developers.google.com/maps/documentation/javascript/reference/polygon?hl=es#Rectangle-Events
			 */
			auto: [
				'bounds_changed',
				'click',
				'contextmenu',
				'dblclick',
				'drag',
				'dragend',
				'dragstart',
				'mousedown',
				'mousemove',
				'mouseout',
				'mouseover',
				'mouseup'
			],
			manual: []
		}
	},
	GmpDrawingManager: {
		noBind: [
			'circleOptions',
			'markerOptions',
			'polygonOptions',
			'polylineOptions',
			'rectangleOptions'
		],
		twoWay: [],
		events: {
			auto: [
				'circlecomplete',
				'markercomplete',
				'polygoncomplete',
				'polylinecomplete',
				'rectanglecomplete'
			],
			manual: [
				'overlaycomplete',
				'update:shapes',
				'added:shape',
				'removed:shape'
			]
		}
	}
};

/**
 * This function helps you to bind events from Google Maps API to Vue events on setup
 *
 * Note: For composition API. This function should be used on setup script.
 *
 * @param eventsComponentConfig - An array of string with all events that you want to bind
 * @param  anyGoogleMapsClassInstance - Any Google Maps instance
 * @param emits - The Vue emit object built with defineEmits function
 * @param excludedEvents - List of events that shouldn't be bind
 *
 * @internal
 */
export function bindEvents(
	eventsComponentConfig: string[],
	anyGoogleMapsClassInstance: Record<string, any>,
	emits: (ev: string, value: any) => void,
	excludedEvents: string[] = []
): void {
	eventsComponentConfig.forEach(eventName => {
		if (!excludedEvents.includes(eventName) && !/_changed/.test(eventName)) {
			anyGoogleMapsClassInstance.value?.addListener(eventName, (ev: any) => {
				emits(eventName, ev);
			});
		}
	});
}

/**
 * Return the configuration for props on every component
 *s
 * @param componentName - The name of the component that should be found
 */
export function getComponentPropsConfig(
	componentName: PluginComponentNames
): Omit<SinglePluginComponentConfig, 'events'> {
	const { events: _events, ...config } = componentConfigs[componentName];
	return config;
}

function bindVuePropsWithGoogleMapsPropsSetters(
	propKey: string,
	props: Record<any, any>,
	trackProperties: string[],
	AnyGoogleMapsClassInstance: Record<string, any>
): { eventName: string; getMethodName: string } {
	const setMethodName = `set${capitalizeFirstLetter(propKey)}`;
	const getMethodName = `get${capitalizeFirstLetter(propKey)}`;
	const eventName = `${propKey.toLowerCase()}_changed`;

	if (
		AnyGoogleMapsClassInstance[setMethodName] &&
		typeof AnyGoogleMapsClassInstance[setMethodName] === 'function'
	) {
		if (
			(typeof props[propKey] !== 'object' && !Array.isArray(props[propKey])) ||
			!trackProperties?.length
		) {
			watch(
				() => props[propKey],
				(value, oldValue) => {
					if (value != null && !isEqual(value, oldValue)) {
						AnyGoogleMapsClassInstance[setMethodName](value);
					}
				},
				{
					immediate: props[propKey] !== undefined,
					deep:
						typeof props[propKey] !== 'object' && !Array.isArray(props[propKey])
				}
			);
		} else {
			watchPrimitivePropertiesOnSetup(
				trackProperties.map(prop => `${propKey}.${prop}`),
				() => {
					AnyGoogleMapsClassInstance[setMethodName](props[propKey]);
				},
				props,
				props[propKey] !== undefined
			);
		}
	}

	return { eventName, getMethodName };
}

export const excludeEventsOnAllComponents = [
	'mousedown',
	'mousemove',
	'mouseout',
	'mouseover',
	'mouseup'
];

/**
 * This function should be used inside setup function or script.
 *
 * Watch the individual properties of a PoD object, instead of the object
 * per se. This is different from a deep watch where both the reference
 * and the individual values are watched.
 *
 * In effect, it throttles the multiple $watch to execute at most once per tick.
 */
export function watchPrimitivePropertiesOnSetup(
	propertiesToTrack: string[],
	handler: () => any,
	props: Record<any, any>,
	immediate = false
): void {
	let isHandled = false;

	function requestHandle(value: any, oldValue: any): void {
		if (!isHandled) {
			isHandled = true;

			nextTick(() => {
				isHandled = false;
				if (value && !isEqual(value, oldValue)) {
					handler();
				}
			});
		}
	}

	propertiesToTrack.forEach(prop => {
		watch(() => props[prop], requestHandle, { immediate });
	});
}

/**
 * Binds the properties defined in props to the google maps instance.
 * If the prop is an Object type, and we wish to track the properties
 * of the object (e.g. the lat and lng of a LatLng), then we do a deep
 * watch. For deep watch, we also prevent the _changed event from being
 * emitted if the data source was external.
 *
 * Note: For composition API. This function should be used on setup script.
 *
 * @param propsComponentConfig - The plugin component configuration for this Google Maps instance
 * @param AnyGoogleMapsClassInstance the Maps, Marker, Circle or any Google Maps class instance
 * @param emits - The Vue emit object built with defineEmits function
 * @param  props - Vue component props  of the component that should be synced with the Google Maps instances props
 * @returns {void} The object which contain all event names to and params that should be used to add listener to the Google Maps instance
 *
 */
export function bindProps(
	propsComponentConfig: Omit<SinglePluginComponentConfig, 'events'>,
	AnyGoogleMapsClassInstance: Record<string, any>,
	emits: (ev: string, value: any) => void,
	props: Record<any, any>
): void {
	const keys = Object.keys(props) as string[];

	for (const propKey of keys) {
		if (propsComponentConfig.noBind.includes(propKey)) {
			return;
		}

		const { eventName, getMethodName } = bindVuePropsWithGoogleMapsPropsSetters(
			propKey,
			props,
			propsComponentConfig?.trackProperties?.value || [],
			AnyGoogleMapsClassInstance
		);

		if (
			propsComponentConfig.twoWay.includes(propKey) &&
			AnyGoogleMapsClassInstance[getMethodName] &&
			typeof AnyGoogleMapsClassInstance[getMethodName] === 'function'
		) {
			AnyGoogleMapsClassInstance?.addListener(eventName, () => {
				const value = AnyGoogleMapsClassInstance[getMethodName]();

				if (value && !isEqual(value, props[propKey])) {
					emits(eventName, value);
				}
			});
		}
	}
}

/**
 * Return the map events attached to the Google Maps Instance
 * @internal
 *
 * @param componentName - The name of the component that should be found
 * @param type
 * @returns - The component events config
 */
export function getComponentEventsConfig(
	componentName: PluginComponentNames,
	type?: 'auto' | 'manual'
): string[] {
	if (type) {
		return componentConfigs[componentName].events[type];
	}

	return [
		...componentConfigs[componentName].events.auto,
		...componentConfigs[componentName].events.manual
	];
}

export function isInsidePolygonBounds(
	polygon: google.maps.Polygon,
	latLng: google.maps.LatLng
) {
	const polygonBounds = new google.maps.LatLngBounds();
	const paths = polygon.getPaths();

	paths.forEach(function (path) {
		path.forEach(function (latLng) {
			polygonBounds.extend(latLng);
		});
	});

	const boundingBox = {
		south: polygonBounds.getSouthWest().lat(),
		west: polygonBounds.getSouthWest().lng(),
		north: polygonBounds.getNorthEast().lat(),
		east: polygonBounds.getNorthEast().lng()
	};

	const bounds = new google.maps.LatLngBounds(
		new google.maps.LatLng(boundingBox.south, boundingBox.west),
		new google.maps.LatLng(boundingBox.north, boundingBox.east)
	);

	return bounds.contains(latLng);
}
